import "react-datepicker/dist/react-datepicker.css";
import "./DatePickerInput.styles.css";

import { getMonth } from "date-fns";
import { fr } from "date-fns/locale/fr";
import DatePicker, {
  type ReactDatePickerProps,
  registerLocale,
  setDefaultLocale,
} from "react-datepicker";
import { customColors } from "tailwind.config";

import ChevronIcon from "$/lib/icons/ChevronIcon";
import { WarningIcon } from "$/lib/icons/WarningIcon";
import { MONTHS } from "$/lib/utils/constants/date.constants";
import { dateTimeFormatter } from "$/lib/utils/constants/intl.constants";
import { cn } from "$/lib/utils/functions/misc.functions";

import type { DatePickerProps } from "./DatePickerInput.types";

registerLocale("fr", fr);
setDefaultLocale("fr");

const DatePickerInput = <
  WithRange extends boolean | undefined = undefined,
  WithMultiple extends boolean | undefined = undefined,
>({
  hintMessage,
  isError,
  errorMessage,
  dateFormat,
  buttonClassName,
  dateFormatter = dateTimeFormatter,
  outerNextMonthButtonDisabled,
  ...calendarProps
}: DatePickerProps & ReactDatePickerProps<WithRange, WithMultiple>) => {
  const formattedSelectedDate = calendarProps.selected
    ? dateFormatter.format(new Date(calendarProps.selected))
    : null;

  return (
    <div className="w-full">
      {hintMessage && (
        <p className="mb-1 text-[13px] font-semibold screen850:text-sm">
          {hintMessage}
        </p>
      )}
      <DatePicker
        locale="fr"
        dateFormat={dateFormat}
        popperPlacement="top"
        popperClassName="relative !top-10 !bottom-10"
        wrapperClassName="w-full"
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          changeYear,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => {
          const currentSelectedYear = monthDate.getFullYear();
          const currentSelectedMonth = monthDate.getMonth();
          const currentYear = new Date().getFullYear();
          const currentMonth = new Date().getMonth();
          const yearsSince1970 = currentYear - 1970;
          const years = Array.from(
            { length: yearsSince1970 + 1 },
            (_, i) => 1970 + i,
          );

          const handleNextMonthButtonClick = () => {
            const selectedDateIsBeforeCurrentDate =
              currentSelectedYear < currentYear ||
              (currentSelectedYear === currentYear &&
                currentSelectedMonth < currentMonth);

            const shouldIncreaseMonth =
              !outerNextMonthButtonDisabled || selectedDateIsBeforeCurrentDate;

            if (shouldIncreaseMonth) increaseMonth();
          };

          return (
            <>
              <div className="mb-2 flex w-full items-center justify-between">
                <button
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  className="flex h-7 w-7 rotate-180 items-center justify-center rounded border border-gray-300"
                  type="button"
                >
                  <ChevronIcon className="size-3" />
                </button>
                <div className="flex flex-1 items-center justify-center gap-2">
                  <select
                    value={currentSelectedYear}
                    onChange={(e) => changeYear(Number(e.target.value))}
                    className="w-fit rounded border py-1 text-sm font-semibold focus:border-selectInputFocusBorder focus:ring-selectInputFocusBorder"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>

                  <span className="text-sm font-semibold">
                    {MONTHS[getMonth(monthDate)]}
                  </span>
                </div>

                <button
                  onClick={handleNextMonthButtonClick}
                  disabled={nextMonthButtonDisabled}
                  className="flex h-7 w-7 items-center justify-center rounded border border-gray-300"
                  type="button"
                >
                  <ChevronIcon className="size-3" />
                </button>
              </div>
              <div className="mt-4 border border-gray-100" />
            </>
          );
        }}
        customInput={
          <button
            type="button"
            aria-invalid={isError}
            className={cn(
              "flex h-12 min-h-12 w-full items-center rounded-lg border border-border bg-white px-4 py-2.5 text-left text-sm text-neutral-900 outline-none duration-150 hover:shadow-sm focus:outline-0 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400 aria-invalid:border-error",
              {
                "hover:border-inputFocusBorder focus:border-inputFocusBorder focus:ring-0":
                  !isError,
              },
              {
                "focus:ring-0 focus:ring-error hover:border-error aria-invalid:focus:border-error":
                  isError,
              },
              buttonClassName,
            )}
          >
            <span>
              {formattedSelectedDate ?? (
                <span className="font-medium text-textInfo">jj/mm/aaaa</span>
              )}
            </span>
          </button>
        }
        {...calendarProps}
      />
      {isError && errorMessage && (
        <p className="mt-1 flex items-center gap-1 text-xs text-error">
          <WarningIcon color={customColors.error.DEFAULT} className="size-4" />
          {errorMessage}
        </p>
      )}
    </div>
  );
};

DatePickerInput.displayName = "DatePickerInput";

export default DatePickerInput;
